import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { Spinner } from "@/shared/components/ui/spinner";
import type { DailyRentalData } from "../../types/dashboard.types";

const BAR_COLOR = "#3b82f6";
const TICK = "#64748b";
const GRID = "rgba(148,163,184,0.08)";

interface DarkTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: DailyRentalData }>;
  label?: string;
}

const DarkTooltip = ({ active, payload }: DarkTooltipProps) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div
      style={{
        backgroundColor: "#0f172a",
        border: "1px solid #1e293b",
        borderRadius: "10px",
        padding: "10px 14px",
        fontSize: 12,
        lineHeight: 1.8,
        boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
      }}
    >
      <p
        style={{
          color: "#94a3b8",
          fontWeight: 500,
          marginBottom: 4,
          fontSize: 11,
        }}
      >
        {d.label}
      </p>
      <p style={{ color: "#93c5fd" }}>
        <span style={{ color: "#64748b", marginRight: 8 }}>Locações</span>
        {d.count}
      </p>
    </div>
  );
};

interface RentalsByDayChartProps {
  data: DailyRentalData[];
  loading?: boolean;
}

export const RentalsByDayChart = ({
  data,
  loading,
}: RentalsByDayChartProps) => (
  <div className="rounded-xl border bg-card p-5 shadow-sm">
    <p className="mb-5 text-sm font-semibold text-foreground">
      Locações por dia
    </p>

    {loading ? (
      <div className="flex h-48 items-center justify-center">
        <Spinner className="size-6" />
      </div>
    ) : data.every((d) => d.count === 0) ? (
      <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
        Sem locações na semana atual
      </div>
    ) : (
      <ResponsiveContainer width="100%" height={192}>
        <BarChart
          data={data}
          margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
          barCategoryGap="40%"
        >
          <CartesianGrid stroke={GRID} vertical={false} />

          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: TICK }}
            axisLine={false}
            tickLine={false}
            tickMargin={8}
            padding={{ left: 8, right: 8 }}
          />

          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 10, fill: TICK }}
            axisLine={false}
            tickLine={false}
            tickCount={4}
            width={28}
          />

          <Tooltip
            content={<DarkTooltip />}
            cursor={{ fill: "rgba(148,163,184,0.06)", radius: 4 }}
          />

          <Bar
            dataKey="count"
            fill={BAR_COLOR}
            radius={[4, 4, 0, 0]}
            maxBarSize={48}
            opacity={0.85}
          />
        </BarChart>
      </ResponsiveContainer>
    )}
  </div>
);
